import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesObjectivesService } from '../../../../services/CoursesObjectives/CoursesObjectivesService'
import { CoursesObjectivesModal } from './CoursesObjectiveModal'

const CoursesObjectivesPage = () => {
  const service = new CoursesObjectivesService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const permissions = {
    addCourseFilter: hasRole('add_course_filter'),
    deleteCourseFilter: hasRole('delete_course_filter')
  }

  const [objectives, setObjectives] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [search, setSearch] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingObjective, setEditingObjective] = useState<{ id: number; name: string } | null>(null)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const fetchObjectives = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.list()
      setObjectives(data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os objetivos', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setFiltered(objectives.filter((objective) => objective.name.toLowerCase().includes(search.toLowerCase())))
  }, [search, objectives])

  useEffect(() => {
    fetchObjectives()
  }, [])

  function openEditModal(objective: { id: number; name: string }) {
    setEditingObjective(objective)
    setIsModalOpen(true)
  }

  function openCreateModal() {
    setEditingObjective(null)
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    setEditingObjective(null)
  }

  function openDeleteDialog(id: number) {
    setIdDeleting(id)
    setIsDeleteOpen(true)
  }

  function closeDeleteDialog() {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  async function handleDelete() {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast.success('Objetivo excluído com sucesso')
      fetchObjectives()
    } catch {
      toast.error('Erro ao excluir objetivo')
    } finally {
      closeDeleteDialog()
    }
  }

  return (
    <>
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por objetivo"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="objetivos" onRetry={fetchObjectives} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="objetivo" />
            ) : (
              filtered.map((objective) => (
                <Tr key={objective.id}>
                  <Td>{objective.id}</Td>
                  <Td>{objective.name}</Td>
                  <Td>{formatDateTime(objective.created_at)}</Td>
                  <Td>
                    {permissions.deleteCourseFilter && (
                      <ActionButton
                        action="edit"
                        onClick={() => openEditModal({ id: objective.id, name: objective.name })}
                      />
                    )}
                    {permissions.deleteCourseFilter && (
                      <ActionButton action="delete" onClick={() => openDeleteDialog(objective.id)} />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {permissions.addCourseFilter && <ActionFab label="Adicionar objetivo de curso" onClick={openCreateModal} />}

      <CoursesObjectivesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onFinished={fetchObjectives}
        initialData={editingObjective}
        service={service}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        action="delete"
        entity="objetivo do curso"
      />
    </>
  )
}

export default CoursesObjectivesPage
